// Spring layout for the eight gap labels, in normalised plot coordinates. Each label is
// pulled to its anchor (fixPoint), pushed away from close neighbours, and kept next to its
// Ex twin. A label moves along one axis only.

export type GType =
  | 'gapDistance'
  | 'gapDistanceEx'
  | 'gap'
  | 'gapEx'
  | 'gapChase'
  | 'gapChaseEx'
  | 'gapAverage'
  | 'gapAverageEx';

type Axis = 'X' | 'Y';

type GEntry = {
  fixPoint: [number, number];
  deltaX: number;
  deltaY: number;
  movesFreeIn: Axis;
};
export type GGeometry = Record<GType, GEntry>;
export type AnchorMap = Record<GType, [number, number]>;

export const G_TYPES: Array<GType> = [
  'gapDistance',
  'gapDistanceEx',
  'gap',
  'gapEx',
  'gapChase',
  'gapChaseEx',
  'gapAverage',
  'gapAverageEx',
];
const G_PAIRS: Array<[GType, GType]> = [
  ['gap', 'gapEx'],
  ['gapDistance', 'gapDistanceEx'],
  ['gapChase', 'gapChaseEx'],
  ['gapAverage', 'gapAverageEx'],
];

// Large start offsets, so labels spring into view on first render.
const ENTRANCE: Record<GType, [Axis, number]> = {
  gapDistance: ['X', 0.08],
  gapDistanceEx: ['X', 0.16],
  gap: ['Y', 0.08],
  gapEx: ['Y', 0.16],
  gapChase: ['Y', -0.08],
  gapChaseEx: ['Y', -0.16],
  gapAverage: ['Y', -0.08],
  gapAverageEx: ['Y', -0.16],
};

const forceOfSpring = 0.35; // pulls line labels/offsets back to their fixpoint
const forceOfPush = 0.18; // pushes nearby lines/labels apart to avoid overlap
const forceOfPairSpring = 0.2; // keeps base and Ex variants visually grouped
const minDist = 0.14;
const animationStep = 0.2;
const damping = 0.88;
const MAX_DELTA = 0.45;

export const clamp = (v: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, v));

const mapTypes = <T>(fn: (type: GType) => T): Record<GType, T> =>
  Object.fromEntries(G_TYPES.map((type) => [type, fn(type)])) as Record<GType, T>;

const getCurrentPoint = (entry: GEntry): [number, number] => [
  entry.fixPoint[0] + entry.deltaX,
  entry.fixPoint[1] + entry.deltaY,
];

export const buildInitialGeometry = (anchors: AnchorMap): GGeometry =>
  mapTypes((type) => {
    const [axis, delta] = ENTRANCE[type];
    return {
      fixPoint: anchors[type],
      deltaX: axis === 'X' ? delta : 0,
      deltaY: axis === 'Y' ? delta : 0,
      movesFreeIn: axis,
    };
  });

// Swaps in new fixPoints but keeps each label where it is on screen, so the springs pull it
// smoothly to its new place.
export const buildTransitionGeometry = (prev: GGeometry, newAnchors: AnchorMap): GGeometry =>
  mapTypes((type) => {
    const g = prev[type];
    const newFix = newAnchors[type];
    const shift = (axis: Axis, delta: number, i: 0 | 1) =>
      g.movesFreeIn === axis
        ? clamp(delta + (g.fixPoint[i] - newFix[i]), -MAX_DELTA, MAX_DELTA)
        : 0;
    return {
      fixPoint: newFix,
      deltaX: shift('X', g.deltaX, 0),
      deltaY: shift('Y', g.deltaY, 1),
      movesFreeIn: g.movesFreeIn,
    };
  });

export function calcNewDeltas(geometry: GGeometry): GGeometry {
  const forces = mapTypes((type1): [number, number] => {
    const g1 = geometry[type1];
    const [x1, y1] = getCurrentPoint(g1);
    let fx = -forceOfSpring * g1.deltaX;
    let fy = -forceOfSpring * g1.deltaY;
    for (const type2 of G_TYPES) {
      if (type1 === type2) continue;
      const [x2, y2] = getCurrentPoint(geometry[type2]);
      const dx = x1 - x2;
      const dy = y1 - y2;
      const distSq = dx * dx + dy * dy;
      if (distSq < minDist * minDist) {
        const dist = Math.sqrt(distSq) || 0.01;
        const push = (forceOfPush * (minDist - dist)) / dist;
        fx += push * dx;
        fy += push * dy;
      }
    }
    return [fx, fy];
  });

  for (const [left, right] of G_PAIRS) {
    const l = geometry[left];
    const r = geometry[right];
    const [lx, ly] = getCurrentPoint(l);
    const [rx, ry] = getCurrentPoint(r);
    const errX = rx - lx - (r.fixPoint[0] - l.fixPoint[0]);
    const errY = ry - ly - (r.fixPoint[1] - l.fixPoint[1]);
    forces[left][0] += forceOfPairSpring * errX;
    forces[left][1] += forceOfPairSpring * errY;
    forces[right][0] -= forceOfPairSpring * errX;
    forces[right][1] -= forceOfPairSpring * errY;
  }

  return mapTypes((type) => {
    const g = geometry[type];
    const [fx, fy] = forces[type];
    const step = (delta: number, force: number) =>
      clamp((delta + force * animationStep) * damping, -MAX_DELTA, MAX_DELTA);
    return {
      fixPoint: g.fixPoint,
      deltaX: g.movesFreeIn === 'X' ? step(g.deltaX, fx) : 0,
      deltaY: g.movesFreeIn === 'Y' ? step(g.deltaY, fy) : 0,
      movesFreeIn: g.movesFreeIn,
    };
  });
}

export function geometryMotion(geometry: GGeometry): number {
  let maxDelta = 0;
  for (const type of G_TYPES) {
    const g = geometry[type];
    maxDelta = Math.max(maxDelta, Math.abs(g.deltaX), Math.abs(g.deltaY));
  }
  return maxDelta;
}
